import { useQuery, useQueryClient } from '@tanstack/react-query'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import { base_colorAlert } from 'ext-ui/style/colors'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import React, { useState } from 'react'
import useDetailTabs from 'src/component-layout/DetailTabs/useDetailTabs'
import MfaStepUpModal from 'src/component-modal/MfaStepUpModal/MfaStepUpModal'
import SvgClose from 'src/components/Svgs/SvgClose'
import { ConfigContext } from 'src/context/ConfigContext/ConfigContextBase'
import { paymentActivityPresentation } from 'src/feature/PaymentHistory/paymentActivityPresentation'
import { paymentFailureReason } from 'src/feature/PaymentHistory/paymentFailurePresentation'
import {
  formatPaymentAmount,
  formatPaymentHistoryDate,
  paymentStatusPresentation,
} from 'src/feature/PaymentHistory/paymentHistoryPresentation'
import { useTranslate } from 'src/localization/Trns'
import { openBillPaymentInvoice } from 'src/queries/payments/payments.download'
import type {
  BillPaymentReconciliationResult,
  BillPaymentSecondaryActionResult,
} from 'src/queries/payments/payments.flow.types'
import {
  paymentsKeys,
  paymentsQueries,
  useCancelBillPayment,
  useResendBillPaymentAuthorization,
  useRestartBillPaymentAuthorization,
} from 'src/queries/payments/payments.queries'
import type {
  BillPaymentDetailsResponse,
  BillPaymentDisplayStatus,
  BillPaymentError,
  BillPaymentHistoryBankLabel,
} from 'src/queries/payments/payments.types'
import { createBillPaymentIdempotencyKey } from 'src/queries/payments/payments.utils'

import style from './PaymentDetailFlyout.style'

interface PaymentDetailFlyoutProps {
  organizationId: string
  viewerUserId: string
  paymentId?: string
  onClose: () => void
}

// The separator inside the mask is a NON-BREAKING space (U+00A0) on purpose
// (EX-40842): it keeps the bullets and the last four on one line. The space
// before the mask stays breakable, so a long bank name still wraps — the whole
// `•••• 1234` unit just moves to the next line together.
const bankLabel = (value: BillPaymentHistoryBankLabel | null): string =>
  value
    ? `${value.label}${value.lastFour ? ` ••••\u00A0${value.lastFour}` : ''}`
    : '—'

const PAYMENT_DETAIL_TABS = ['overview', 'activity'] as const

const PaymentDetailFlyout: React.FC<PaymentDetailFlyoutProps> = ({
  organizationId,
  viewerUserId,
  paymentId,
  onClose,
}) => {
  const translate = useTranslate()
  const { locale } = React.useContext(ConfigContext)
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'overview' | 'activity'>('overview')
  const [mfaDetails, setMfaDetails] = useState<BillPaymentDetailsResponse>()
  const [actionError, setActionError] = useState<string>()
  const { getPanelProps, getTabProps } = useDetailTabs({
    activeTab: tab,
    onTabChange: setTab,
    tabs: PAYMENT_DETAIL_TABS,
  })
  const detailQuery = useQuery(
    paymentsQueries.paymentDetail(organizationId, viewerUserId, paymentId),
  )
  // Polls a provider-free status endpoint (EX-40744). The detail query itself
  // cannot be polled — it runs enrichDetail, which makes two live Modern
  // Treasury calls per read.
  const statusQuery = useQuery(
    paymentsQueries.paymentStatus(organizationId, viewerUserId, paymentId),
  )
  const cancelPayment = useCancelBillPayment(organizationId, viewerUserId)
  const resendAuthorization = useResendBillPaymentAuthorization(
    organizationId,
    viewerUserId,
  )
  const restartAuthorization = useRestartBillPaymentAuthorization(
    organizationId,
    viewerUserId,
  )
  const details = detailQuery.data
  const history = details?.history
  const attachmentId = history?.payment.invoiceAttachment?.id
  const attachmentQuery = useQuery(
    paymentsQueries.invoiceAttachment(organizationId, attachmentId),
  )
  const actions = history?.payment.availableActions ?? []
  // Processor-agnostic plain-language reason for a terminal FAILED / RETURNED
  // payment, read from the enriched top-level execution detail. `null` for
  // non-terminal statuses or when the detail is absent (rolling deploy).
  const failureReason = paymentFailureReason(history?.execution)

  // The status Pill has no live-region semantics, so a status change that
  // happens without user action (a refetch resolving to a new displayStatus)
  // is silent for screen-reader users. Announce the CHANGE only, and only for
  // transitions no other live region already covers.
  //
  // ⚠️ This component is mounted ONCE by PaymentsPage and reused: `paymentId`
  // changes as rows are opened, and becomes undefined on close (see
  // PaymentsPage's <PaymentDetailFlyout/>, rendered whenever organizationId
  // resolves). So the remembered status MUST be scoped to the payment it came
  // from — otherwise opening payment B after payment A changed status compares
  // B's opening status against A's and fires a false announcement, with A's
  // stale text still in the live region.
  const displayStatus = history?.payment.displayStatus
  const trackedStatus = React.useRef<{
    paymentId: string | undefined
    status: BillPaymentDisplayStatus | undefined
  }>({ paymentId: undefined, status: undefined })
  const [statusAnnouncement, setStatusAnnouncement] = useState<string | null>(
    null,
  )
  React.useEffect(() => {
    const tracked = trackedStatus.current

    // Different payment (or the flyout closed): re-baseline and drop any
    // message from the previous payment.
    if (tracked.paymentId !== paymentId) {
      trackedStatus.current = { paymentId, status: displayStatus }
      setStatusAnnouncement(null)
      return
    }

    if (!displayStatus || tracked.status === displayStatus) return
    const previous = tracked.status
    trackedStatus.current = { paymentId, status: displayStatus }

    // First status observed for this payment — that's the opening state, not a
    // change, so stay silent.
    if (previous === undefined) return

    // FAILED/RETURNED already mount a severe AlertPanel, which is itself a
    // live region; announcing here too would read the change out twice.
    if (displayStatus === 'FAILED' || displayStatus === 'RETURNED') return

    setStatusAnnouncement(
      translate('Payment status updated to ${status}', {
        status: translate(paymentStatusPresentation(displayStatus).label),
      }),
    )
  }, [paymentId, displayStatus, translate])

  // Refetch the (expensive) detail when the (cheap) poll reports a NEW status.
  //
  // ⚠️ Key this on `displayStatus`, NOT on `statusQuery.data`. The poll returns a
  // fresh object every tick (and `execution.statusUpdatedAt` moves on its own),
  // so an object dependency refetches the detail every tick — two Modern
  // Treasury calls each — rebuilding the amplification this split removes.
  //
  // Scoped by paymentId like the announcer above: one mount serves every payment.
  const polledStatus = statusQuery.data?.displayStatus
  const trackedPolledStatus = React.useRef<{
    paymentId: string | undefined
    status: BillPaymentDisplayStatus | undefined
  }>({ paymentId: undefined, status: undefined })
  const refetchDetail = detailQuery.refetch
  React.useEffect(() => {
    const tracked = trackedPolledStatus.current

    if (tracked.paymentId !== paymentId) {
      // Seed from the DETAIL, not the poll: a poll that beat the detail is
      // exactly the value we still owe a refetch for.
      trackedPolledStatus.current = { paymentId, status: displayStatus }
      return
    }

    // The opening baseline is the detail's own status, NOT merely the first
    // value the poll happened to return. When the first status request fails
    // the poll contributes `undefined`; without falling back to the detail, the
    // NEXT poll is mistaken for the opening state and skipped — and if that poll
    // is terminal it also stops the polling, stranding the flyout on a stale
    // detail with nothing left to correct it. (Ryan, EX-40744 review.)
    const baseline = tracked.status ?? displayStatus

    // A failed poll leaves the previous value in place; it must never refetch.
    if (polledStatus === undefined || baseline === polledStatus) {
      if (baseline !== tracked.status) {
        trackedPolledStatus.current = { paymentId, status: baseline }
      }
      return
    }

    // No baseline yet, so leave the tracker UNCOMMITTED. `/status` is a local
    // read while the detail waits on two provider calls, so the poll often wins
    // that race; committing it here would make the detail's older status look
    // like a match on arrival and consume the refetch for good.
    if (baseline === undefined) return

    trackedPolledStatus.current = { paymentId, status: polledStatus }
    void refetchDetail()
  }, [paymentId, polledStatus, displayStatus, refetchDetail])

  const invalidateHistory = (): void => {
    void queryClient.invalidateQueries({
      queryKey: paymentsKeys.paymentHistories(),
    })
  }

  const reconcileHistory = async (): Promise<void> => {
    invalidateHistory()
    await detailQuery.refetch()
  }

  const openResume = async (): Promise<void> => {
    if (!details) return
    setActionError(undefined)
    if (details.authorization) {
      setMfaDetails(details)
      return
    }
    try {
      const restarted = await restartAuthorization.mutateAsync({
        paymentId: details.payment.id,
        revision: details.payment.revision,
        idempotencyKey: createBillPaymentIdempotencyKey(),
      })
      setMfaDetails(restarted)
    } catch {
      setActionError(
        translate('We could not resume this payment. Please try again.'),
      )
      await reconcileHistory()
    }
  }

  const updateMfaDetails = (
    next: BillPaymentDetailsResponse,
  ): BillPaymentSecondaryActionResult => {
    setMfaDetails(next)
    return { kind: 'updated', details: next }
  }

  const reconcile = async (
    error: BillPaymentError,
  ): Promise<BillPaymentReconciliationResult> => {
    const result = await detailQuery.refetch()
    if (!result.data) return { kind: 'unavailable', error }
    setMfaDetails(result.data)
    if (result.data.payment.state === 'SCHEDULED') {
      return { kind: 'scheduled', details: result.data }
    }
    if (result.data.authorization) {
      return {
        kind: 'authorization',
        details: result.data,
        sameAttempt: false,
      }
    }
    return { kind: 'terminal', details: result.data }
  }

  const cancel = async (): Promise<void> => {
    if (!details) return
    setActionError(undefined)
    try {
      await cancelPayment.mutateAsync({
        paymentId: details.payment.id,
        revision: details.payment.revision,
        idempotencyKey: createBillPaymentIdempotencyKey(),
      })
    } catch (error) {
      const code =
        error && typeof error === 'object' && 'errorCode' in error
          ? String(error.errorCode)
          : undefined
      if (code === 'EXECUTION_IN_FLIGHT') {
        setActionError(
          translate(
            'This payment is already processing and can no longer be cancelled.',
          ),
        )
      } else if (code === 'PAYMENT_PRECONDITION_FAILED') {
        setActionError(
          translate('This payment changed. We refreshed the latest details.'),
        )
      } else {
        setActionError(
          translate('We could not cancel this payment. Please try again.'),
        )
      }
      await reconcileHistory()
    }
  }

  const downloadInvoice = async (): Promise<void> => {
    if (!attachmentId) return
    setActionError(undefined)
    try {
      await openBillPaymentInvoice(queryClient, organizationId, attachmentId)
    } catch {
      setActionError(
        translate('We could not download this invoice. Please try again.'),
      )
    }
  }

  return (
    <>
      <BaseFlyout
        className="PaymentDetailFlyout"
        css={style}
        isOpen={Boolean(paymentId)}
        onClose={() => {
          setTab('overview')
          setMfaDetails(undefined)
          setActionError(undefined)
          onClose()
        }}
        aria-label={translate('Payment details')}
      >
        {detailQuery.isLoading ? (
          <div className="payment-detail-message">
            {translate('Loading payment…')}
          </div>
        ) : detailQuery.isError || !details ? (
          <div className="payment-detail-message">
            <p>{translate('We could not load this payment.')}</p>
            <Button
              variant="secondary"
              onClick={() => void detailQuery.refetch()}
            >
              {translate('Retry')}
            </Button>
          </div>
        ) : !history ? (
          <div className="payment-detail-message">
            <h2>{translate('Payment details')}</h2>
            <p>
              {translate(
                'Detailed payment history is temporarily unavailable. Please refresh in a moment.',
              )}
            </p>
          </div>
        ) : (
          <div className="payment-detail-layout">
            <header className="payment-detail-header">
              <h2>{history.payment.vendor.name}</h2>
              <div className="payment-detail-subtitle">
                <span>
                  {translate(
                    history.payment.displayStatus === 'COMPLETED'
                      ? 'Paid ${date}'
                      : 'Due ${date}',
                    {
                      date: formatPaymentHistoryDate(
                        history.payment.displayStatus === 'COMPLETED'
                          ? history.paymentDate
                          : history.payment.invoiceDueDate,
                        locale,
                      ),
                    },
                  )}
                </span>
                <span aria-hidden="true">{'·'}</span>
                <span>
                  {formatPaymentAmount(
                    history.payment.amountCents,
                    history.payment.currency,
                    locale,
                  )}
                </span>
                <span aria-hidden="true">{'·'}</span>
                <span>{`#${history.payment.invoiceNumber}`}</span>
                <span aria-hidden="true">{'·'}</span>
                <Pill
                  size="medium"
                  color={
                    paymentStatusPresentation(history.payment.displayStatus)
                      .color
                  }
                >
                  {translate(
                    paymentStatusPresentation(history.payment.displayStatus)
                      .label,
                  )}
                </Pill>
              </div>
            </header>
            {/* Polite, atomic announcement of a status change the user did not
                trigger. Visually hidden — the Pill above is the sighted
                affordance; this only exists to give the same update to screen
                readers (WCAG 2.1 AA 4.1.3). */}
            <p
              className="payment-status-announcement"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {statusAnnouncement}
            </p>
            {history.payment.displayStatus === 'FAILED' && (
              <div className="payment-detail-alert">
                <AlertPanel variant="severe" role="alert">
                  <strong>{translate('Payment failed')}</strong>
                  <span>
                    {translate(
                      failureReason ?? 'This payment could not be completed.',
                    )}
                  </span>
                </AlertPanel>
              </div>
            )}
            {history.payment.displayStatus === 'RETURNED' && (
              <div className="payment-detail-alert">
                <AlertPanel variant="severe" role="alert">
                  <strong>{translate('Payment returned')}</strong>
                  <span>
                    {translate(
                      failureReason ?? 'The payment was returned by the bank.',
                    )}
                  </span>
                </AlertPanel>
              </div>
            )}
            {actionError && (
              <div className="payment-detail-alert">
                <AlertPanel variant="severe" role="alert">
                  {actionError}
                </AlertPanel>
              </div>
            )}
            <div
              className="payment-detail-tabs"
              role="tablist"
              aria-label={translate('Payment details')}
            >
              <button {...getTabProps('overview')}>
                {translate('Overview')}
              </button>
              <button {...getTabProps('activity')}>
                {translate('Payment Activity')}
              </button>
            </div>
            <div className="payment-detail-scroll">
              {tab === 'overview' ? (
                <div {...getPanelProps('overview')}>
                  <section>
                    <h3>{translate('Invoice Details')}</h3>
                    <dl>
                      <div>
                        <dt>{translate('Amount')}</dt>
                        <dd>
                          {formatPaymentAmount(
                            history.payment.amountCents,
                            history.payment.currency,
                            locale,
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Due Date')}</dt>
                        <dd>
                          {formatPaymentHistoryDate(
                            history.payment.invoiceDueDate,
                            locale,
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Invoice Number')}</dt>
                        <dd>{history.payment.invoiceNumber}</dd>
                      </div>
                      <div>
                        <dt>{translate('Invoice Date')}</dt>
                        <dd>
                          {formatPaymentHistoryDate(history.invoiceDate, locale)}
                        </dd>
                      </div>
                      {history.payment.invoiceAttachment && (
                        <div>
                          <dt>{translate('Invoice Attachment')}</dt>
                          <dd>
                            <button
                              type="button"
                              className="payment-detail-link"
                              aria-busy={attachmentQuery.isLoading}
                              onClick={() => void downloadInvoice()}
                            >
                              {attachmentQuery.data?.filename ??
                                translate('Download')}
                            </button>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </section>
                  <section>
                    <h3>{translate('Payment Details')}</h3>
                    <dl>
                      <div>
                        <dt>{translate('Payment Method')}</dt>
                        <dd>{bankLabel(history.paymentMethod)}</dd>
                      </div>
                      <div>
                        <dt>{translate('Vendor Bank Account')}</dt>
                        <dd>
                          {history.vendorBankAccount
                            ? bankLabel(history.vendorBankAccount)
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Bill Payer Bank Account')}</dt>
                        <dd>
                          {history.payerBankAccount
                            ? bankLabel(history.payerBankAccount)
                            : '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Payment Date')}</dt>
                        <dd>
                          {formatPaymentHistoryDate(history.paymentDate, locale)}
                        </dd>
                      </div>
                      <div>
                        <dt>{translate('Vendor Remittance Email')}</dt>
                        <dd>{history.remittanceEmail ?? '—'}</dd>
                      </div>
                      <div>
                        <dt>{translate('Payment Sent By')}</dt>
                        <dd>
                          {history.payment.sender.displayName ??
                            history.payment.sender.email ??
                            '—'}
                        </dd>
                      </div>
                    </dl>
                  </section>
                  {history.memo && (
                    <section>
                      <h3>{translate('Memo')}</h3>
                      <p>{history.memo}</p>
                    </section>
                  )}
                </div>
              ) : (
                <section {...getPanelProps('activity')}>
                  <h3>{translate('Status')}</h3>
                  <ol className="payment-activity-list">
                    {history.activities.map((activity, index) => {
                      const presentation = paymentActivityPresentation(
                        activity,
                        index === 0,
                      )
                      const olderActivity = history.activities[index + 1]
                      const connectorState = olderActivity
                        ? paymentActivityPresentation(olderActivity, false)
                            .state
                        : undefined
                      const actor =
                        activity.actor?.displayName ??
                        activity.actor?.email ??
                        translate('Extend')

                      return (
                        <li
                          key={activity.id}
                          data-state={presentation.state}
                          data-connector-state={connectorState}
                          aria-current={
                            presentation.state === 'current'
                              ? 'step'
                              : undefined
                          }
                        >
                          <span
                            className="payment-activity-marker"
                            aria-hidden="true"
                          >
                            {presentation.state === 'completed' && (
                              <CheckmarkIcon />
                            )}
                            {presentation.state === 'current' && (
                              <span className="payment-activity-current-dot" />
                            )}
                            {presentation.state === 'error' && (
                              <SvgClose stroke={base_colorAlert} />
                            )}
                          </span>
                          <strong>{translate(presentation.label)}</strong>
                          <span className="payment-activity-meta">
                            <time dateTime={activity.createdAt}>
                              {new Date(activity.createdAt).toLocaleString(
                                locale,
                              )}
                            </time>
                            <span aria-hidden="true">{'·'}</span>
                            <span>{actor}</span>
                          </span>
                          {(activity.displayStatus === 'FAILED' ||
                            activity.displayStatus === 'RETURNED') &&
                            failureReason && (
                              <span className="payment-activity-reason">
                                {translate(failureReason)}
                              </span>
                            )}
                        </li>
                      )
                    })}
                  </ol>
                </section>
              )}
            </div>
            {actions.length > 0 && (
              <footer className="payment-detail-footer">
                {actions.includes('CANCEL') && (
                  <Button
                    variant="secondary"
                    disabled={cancelPayment.isPending}
                    onClick={() => void cancel()}
                  >
                    {translate('Cancel payment')}
                  </Button>
                )}
                {actions.includes('RESUME_AUTHORIZATION') && (
                  <Button variant="primary" onClick={() => void openResume()}>
                    {translate('Resume payment')}
                  </Button>
                )}
              </footer>
            )}
          </div>
        )}
      </BaseFlyout>
      {mfaDetails?.authorization && (
        <MfaStepUpModal
          open
          payment={mfaDetails.payment}
          authorization={mfaDetails.authorization}
          onClose={() => setMfaDetails(undefined)}
          onResend={async () => {
            const authorization = mfaDetails.authorization
            if (!authorization) {
              return { kind: 'terminal', details: mfaDetails }
            }
            return updateMfaDetails(
              await resendAuthorization.mutateAsync({
                paymentId: mfaDetails.payment.id,
                revision: mfaDetails.payment.revision,
                authorizationId: authorization.authorizationId,
                idempotencyKey: createBillPaymentIdempotencyKey(),
              }),
            )
          }}
          onRestart={async () =>
            updateMfaDetails(
              await restartAuthorization.mutateAsync({
                paymentId: mfaDetails.payment.id,
                revision: mfaDetails.payment.revision,
                idempotencyKey: createBillPaymentIdempotencyKey(),
              }),
            )
          }
          onReconcileSchedule={reconcile}
          onScheduled={() => {
            setMfaDetails(undefined)
          }}
          onTerminalError={() => {
            setMfaDetails(undefined)
            void reconcileHistory()
          }}
        />
      )}
    </>
  )
}

export default PaymentDetailFlyout
